import { useEffect, useState, useRef, lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Calculator, FileText, Building, BookOpen, Shield, Award, Users, Star, MessageCircle, ArrowRight, CheckCircle, TrendingUp, Quote, Lock, Globe, Mail, CheckCircle2, FileSpreadsheet, Activity, Monitor, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import AdUnit from "@/components/AdUnit";
import { motion, useInView, useSpring, useMotionValue, useScroll, useTransform, AnimatePresence } from "framer-motion";

// Lazy load components that are not immediately visible
const Footer = lazy(() => import("@/components/Footer"));
const WhatsAppButton = lazy(() => import("@/components/WhatsAppButton"));
import ScrollToTop from "@/components/ScrollToTop";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { SEO } from "@/components/SEO";
import { DynamicIcon } from "@/components/IconMapper";

const DesktopIndex = () => {
  const { toast } = useToast();
  const [subscribeEmail, setSubscribeEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState<"success" | "error" | null>(null);
  const [subscribeMessage, setSubscribeMessage] = useState("");
  const [featuredServices, setFeaturedServices] = useState<any[]>([]);
  const [loadingServices, setLoadingServices] = useState(true);

  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 1000], [0, 150]);
  const y2 = useTransform(scrollY, [0, 1000], [0, -100]);
  const y3 = useTransform(scrollY, [0, 1000], [0, 80]);

  const fadeUpVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const reviews = [
    { text: "Incredible service! Saved me so much time. I can focus on my work while they handle the FBR headaches.", author: "Ali Khan", role: "Freelancer" },
    { text: "Make Me Filer completely transformed how I handle my yearly taxes. Highly professional and deeply knowledgeable.", author: "Sarah Ahmed", role: "Business Owner" },
    { text: "Their transparent pricing and excellent customer support won me over. Filing has never felt this easy.", author: "Usman Tariq", role: "Software Engineer" },
    { text: "They registered my private limited company in record time! The entire process was seamless and stress-free.", author: "Zainab Raza", role: "CEO, TechNova" },
    { text: "A truly modern platform. The encrypted document area gives me peace of mind regarding my confidential data.", author: "Omer Farooq", role: "Consultant" }
  ];

  const [activeReviewIndex, setActiveReviewIndex] = useState(0);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await api.get("/data/packages?is_active=eq.true&category=eq.main_service&_order=created_at.desc&_limit=4");
        setFeaturedServices(data || []);
      } catch (err) {
        console.error("Failed to fetch featured services", err);
      } finally {
        setLoadingServices(false);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveReviewIndex((prev) => (prev + 1) % reviews.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [reviews.length]);

  // SVG Data URI for faint background pattern using the MF logo outline/solid
  const generateBgImage = () => {
    return `url('data:image/svg+xml;utf8,<svg viewBox="0 0 140 100" xmlns="http://www.w3.org/2000/svg" width="80" height="80" opacity="0.025"><path d="M 10 16 C 10 10, 16 10, 26 10 L 34 10 L 34 90 C 34 96, 28 96, 18 96 L 10 96 Z" fill="%230d3f74" /><path d="M 30 14 L 70 65 L 53 78 L 14 26 Z" fill="%230d3f74" /><path d="M 45 78 L 92 14 L 110 26 L 65 92 Z" fill="%230d3f74" /><path d="M 88 10 L 112 10 L 112 90 C 112 96, 106 96, 96 96 L 88 96 Z" fill="%230d3f74" /><path d="M 104 10 L 132 10 C 138 10, 140 12, 140 18 L 140 24 C 140 30, 138 32, 132 32 L 104 32 Z" fill="%230d3f74" /><path d="M 104 44 L 126 44 C 132 44, 134 46, 134 52 L 134 58 C 134 64, 132 66, 126 66 L 104 66 Z" fill="%230d3f74" /></svg>')`;
  }

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subscribeEmail) return;

    setIsSubscribing(true);
    try {
      // api.post throws an error on failure directly. For success, it just returns the data.
      await api.post('/data/subscribers', { email: subscribeEmail });
      
      setSubscribeMessage("Subscribed Successfully! You will now receive the latest tax updates.");
      setSubscribeStatus("success");
    } catch (err: any) {
      const errMessage = err?.message?.toLowerCase() || '';
      
      // If the error mentions uniqueness or already exists, treat it as a specific inline error
      if (errMessage.includes('duplicate') || errMessage.includes('unique') || err?.code === '23505') {
          setSubscribeMessage("This email is already subscribed! Try a different one.");
          setSubscribeStatus("error");
      } else {
          toast({
            title: "Subscription Failed",
            description: err.message || "Something went wrong. Please try again.",
            variant: "destructive",
          });
      }
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden font-sans text-slate-900">
      <SEO 
        title="Make Me Filer - Easy NTN & Tax Return Filing in Pakistan" 
        description="FBR Tax Filer and NTN Registration services made easy. Over 10,000 satisfied clients across Pakistan and Azad Kashmir."
      />
      <Header />

      {/* Main Container */}
      <div 
        className="w-full relative" 
        style={{ backgroundImage: generateBgImage(), backgroundSize: '200px 200px', backgroundPosition: 'center center' }}
      >
        {/* Subtle Grid Overlay */}
        <div className="absolute inset-0 max-w-[100vw] overflow-hidden bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_70%_70%_at_50%_50%,#000_30%,transparent_100%)] pointer-events-none z-0"></div>

        {/* Hero Section */}
        <section className="relative min-h-[45vh] lg:min-h-[85vh] flex flex-col justify-center pt-8 pb-8 lg:py-4 px-4 sm:px-6 lg:px-8 w-full overflow-hidden border-b border-white z-10">
          
          {/* Faint Glows */}
          <div className="absolute top-[20%] right-[10%] w-[500px] h-[500px] bg-blue-100 rounded-full blur-[100px] opacity-40 -z-10 pointer-events-none"></div>
          <div className="absolute bottom-[-10%] left-[20%] w-[400px] h-[400px] bg-yellow-100 rounded-full blur-[100px] opacity-30 -z-10 pointer-events-none"></div>

          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
              
              {/* Left Content */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeUpVariants}
                className="text-center lg:text-left pt-10"
              >
                <div className="inline-block mt-4 mb-8">
                  <Badge className="bg-white text-blue-600 px-4 py-2 text-sm font-semibold rounded-full border border-blue-100/50 shadow-sm inline-flex items-center gap-2">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    #1 Tax Consultants in Azad Kashmir
                  </Badge>
                </div>

                <motion.h1 
                  initial="hidden"
                  animate="visible"
                  variants={staggerContainer}
                  className="text-3xl sm:text-[4rem] lg:text-[5rem] font-extrabold text-[#1a2332] mb-4 lg:mb-6 leading-[1.2] lg:leading-[1.1] tracking-tight"
                >
                  <motion.span variants={itemVariants} className="inline-block">Tax Filing</motion.span> <br />
                  <motion.span variants={itemVariants} className="text-blue-600 inline-block">Made Simple.</motion.span>
                </motion.h1>

                <motion.p variants={itemVariants} className="text-base sm:text-xl text-slate-500 mb-8 lg:mb-10 leading-relaxed max-w-xl mx-auto lg:mx-0 font-medium">
                  Expert tax services for individuals and businesses. We handle compliance so you can focus on growth.
                </motion.p>

                <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button size="lg" asChild className="h-14 px-8 text-base font-semibold rounded-full bg-blue-600 hover:bg-blue-700 shadow-md transition-all group w-full sm:w-auto">
                      <Link to="/tax-return-info">
                        File Now
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </motion.div>
                </motion.div>

                <div className="mt-8 lg:mt-12 flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4 text-slate-600 border-t border-slate-100 pt-6">
                  <div className="flex items-center gap-1.5 sm:gap-2 bg-white px-3 py-1.5 rounded-full border border-slate-200 shadow-sm">
                    <Shield className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" />
                    <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-700">Certified Consultants</span>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2 bg-white px-3 py-1.5 rounded-full border border-slate-200 shadow-sm">
                    <Star className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500 fill-yellow-500" />
                    <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-700">1000+ Clients</span>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2 bg-white px-3 py-1.5 rounded-full border border-slate-200 shadow-sm">
                     <Lock className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                     <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-700">100% Secure</span>
                  </div>
                </div>
              </motion.div>

              {/* Right Hero Image and Floating Cards */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="hidden lg:flex relative lg:h-[650px] w-full items-center justify-center"
              >
                <div className="relative w-full max-w-[650px] rounded-[2.5rem] overflow-visible z-10 p-2 lg:-mr-8">
                   {/* Main Image Container */}
                   <div className="relative rounded-[2rem] overflow-hidden border-[10px] border-white shadow-2xl bg-white w-[90%] mx-auto ml-10">
                    <img
                      src="/hero_office_glass_logo_1765041860613.webp"
                      srcSet="/hero_office_494w.webp 494w, /hero_office_glass_logo_1765041860613.webp 750w"
                      sizes="(max-width: 768px) 100vw, 500px"
                      fetchPriority="high"
                      alt="Professional Tax Office"
                      className="w-full h-auto aspect-[4/3] object-cover"
                    />
                   </div>

                   {/* Floating Top Left Card */}
                   <motion.div style={{ y: y1 }} className="absolute top-[8%] left-[-5%] sm:left-[-2%] bg-white p-4 rounded-2xl shadow-xl flex items-center gap-4 z-20 border border-slate-100 pr-8 animate-float-slow">
                     <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-xl flex justify-center items-center">
                        <FileText className="w-6 h-6" />
                     </div>
                     <div>
                       <h2 className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">SERVICE</h2>
                       <h3 className="text-sm font-bold text-slate-800">Tax Filing</h3>
                     </div>
                   </motion.div>

                   {/* Floating Mid Right Card */}
                   <motion.div style={{ y: y2 }} className="absolute top-[45%] right-[-10%] sm:right-[-5%] md:right-[0%] bg-white p-4 rounded-2xl shadow-xl flex items-center gap-4 z-20 border border-slate-100 pr-8 animate-float-slower">
                     <div className="w-12 h-12 bg-red-100 text-red-500 rounded-xl flex justify-center items-center">
                        <Activity className="w-6 h-6" />
                     </div>
                     <div>
                       <h2 className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">GOAL</h2>
                       <h3 className="text-sm font-bold text-slate-800">Business Growth</h3>
                     </div>
                   </motion.div>

                   {/* Floating Bottom Right Card */}
                   <motion.div style={{ y: y3 }} className="absolute bottom-[5%] right-[-5%] sm:right-0 bg-white p-4 rounded-2xl shadow-xl flex items-center gap-4 z-20 border border-slate-100 pr-8 animate-float">
                     <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex justify-center items-center">
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                     </div>
                     <div>
                       <h2 className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">SOLUTION</h2>
                       <h3 className="text-sm font-bold text-slate-800">Tax Planning</h3>
                     </div>
                   </motion.div>

                </div>
              </motion.div>

            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 my-8 relative z-20">
          <AdUnit slotId="8659216077" label="Home Top" />
        </div>

        <section className="relative min-h-[50vh] lg:min-h-[75vh] flex flex-col justify-center py-8 lg:py-12 bg-slate-50/50 backdrop-blur-3xl border-b border-white z-20">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 lg:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 lg:mb-4">
                Designed for Your <span className="text-blue-600">Peace of Mind</span>
              </h2>
              <p className="text-sm sm:text-base text-slate-500 max-w-2xl mx-auto">
                We combine technology with expertise to give you Pakistan's most reliable tax experience.
              </p>
            </div>

            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {/* Feature 1 */}
              <motion.div 
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                className="bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center flex flex-col items-center transition-shadow duration-300 hover:shadow-xl"
              >
                <div className="w-16 h-16 bg-white rounded-full shadow-sm border border-slate-100 flex items-center justify-center mb-6">
                  {/* Fake Google Logo */}
                  <svg viewBox="0 0 24 24" className="w-8 h-8"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                </div>
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" />)}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">5-Star Rated</h3>
                <p className="text-sm text-slate-500">Trusted by hundreds of happy clients on Google.</p>
              </motion.div>

              {/* Feature 2 */}
              <motion.div 
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                className="bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center flex flex-col items-center transition-shadow duration-300 hover:shadow-xl"
              >
                <div className="w-16 h-16 bg-green-50 text-green-600 rounded-full flex items-center justify-center mb-6">
                  <FileSpreadsheet className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2 mt-7">Competitive Pricing</h3>
                <p className="text-sm text-slate-500">Transparent rates. No hidden office fees.</p>
              </motion.div>

              {/* Feature 3 */}
              <motion.div 
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                className="bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center flex flex-col items-center relative transition-shadow duration-300 hover:shadow-xl"
              >
                <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-6 relative">
                  <Lock className="w-8 h-8" />
                  <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-0.5 border-2 border-white">
                    <CheckCircle2 className="w-3 h-3" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2 mt-7">100% Secure</h3>
                <p className="text-sm text-slate-500">Encrypted document area. Your data is strictly confidential.</p>
              </motion.div>

              {/* Feature 4 */}
              <motion.div 
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                className="bg-white rounded-3xl p-8 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center flex flex-col items-center transition-shadow duration-300 hover:shadow-xl"
              >
                <div className="w-16 h-16 bg-purple-50 text-purple-600 rounded-full flex items-center justify-center mb-6">
                  <Monitor className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2 mt-7">Easy Web App</h3>
                <p className="text-sm text-slate-500">User friendly interface available in both <span className="text-purple-600 font-semibold">Urdu</span> & English.</p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <section className="relative min-h-[50vh] lg:min-h-[80vh] flex flex-col justify-center py-8 lg:py-12 z-20 border-b border-white">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              
              {/* Left Image */}
              <motion.div 
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="relative"
              >
                <div className="absolute inset-y-8 -left-8 w-24 border-l-2 border-b-2 border-blue-200 rounded-bl-[3rem] -z-10"></div>
                <div className="bg-white w-full rounded-[2.5rem] shadow-2xl overflow-hidden aspect-[4/5] sm:aspect-square lg:aspect-[4/3] object-cover">
                  <motion.img 
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.8 }}
                    src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=75"
                    srcSet="
                      https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=75 400w,
                      https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=75 800w,
                      https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=75 1200w
                    "
                    sizes="(max-width: 768px) 100vw, 50vw"
                    loading="lazy"
                    alt="Team meeting" 
                    className="w-full h-full object-cover" 
                  />
                </div>
              </motion.div>

              {/* Right Content */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={staggerContainer}
              >
                <motion.div variants={itemVariants} className="flex items-center gap-2 text-blue-600 font-bold tracking-wider text-xs uppercase mb-4">
                  <div className="w-1.5 h-4 bg-blue-600 rounded-sm"></div>
                  Who we are
                </motion.div>
                <motion.h2 variants={itemVariants} className="text-2xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 lg:mb-6 leading-tight">
                  Why Choose <span className="text-blue-600">Make Me Filer</span>
                </motion.h2>
                <motion.p variants={itemVariants} className="text-sm sm:text-base text-slate-600 mb-6 lg:mb-8 leading-relaxed font-medium">
                  We are a team of dedicated tax professionals committed to providing exceptional service to individuals and businesses across Azad Kashmir and Pakistan. Our expertise ensures your tax matters are handled with precision and care.
                </motion.p>

                <ul className="space-y-4 lg:space-y-5">
                  {[
                    "Over 10 years of professional experience",
                    "Licensed and certified tax consultants",
                    "Expertise in local and federal tax laws",
                    "Personalized service for every client",
                    "Transparent pricing with no hidden fees",
                    "Quick turnaround time on all services"
                  ].map((text, i) => (
                    <motion.li variants={itemVariants} key={i} className="flex items-start gap-4 text-slate-700 font-medium">
                      <div className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5 border border-blue-100">
                        <CheckCircle2 className="w-4 h-4" />
                      </div>
                      <span className="text-sm sm:text-base">{text}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

            </div>
          </div>
        </section>

        <section className="relative min-h-[50vh] lg:min-h-[80vh] flex flex-col justify-center py-8 lg:py-10 z-20 bg-slate-50/50 backdrop-blur-xl border-b border-white">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-[1fr_2fr] gap-8 lg:gap-16 items-center">
              
              {/* Left Column - Heading */}
              <div className="lg:self-center">
                <Badge className="bg-white text-blue-600 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold rounded-full border border-blue-100 shadow-sm mb-4 lg:mb-6">
                  World Class Expertise
                </Badge>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 leading-tight mb-4 lg:mb-6">
                  Our <br className="hidden sm:block" />Professional <br /><span className="text-blue-600">Services</span>
                </h2>
                <p className="text-sm sm:text-lg text-slate-600 mb-6 leading-relaxed font-medium">
                  Comprehensive solutions designed to scale with your business. From filing to forecasting, we cover it all.
                </p>
                <Button size="lg" className="rounded-full bg-blue-600 hover:bg-blue-700 px-8">
                  View All Services
                </Button>
              </div>

              {/* Right Column - Grid */}
              <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={staggerContainer}
                className="grid sm:grid-cols-2 gap-4 lg:gap-6"
              >
                
                {loadingServices ? (
                  <div className="col-span-1 sm:col-span-2 text-center py-10 text-slate-500">Loading services...</div>
                ) : featuredServices.length > 0 ? (
                  featuredServices.map((service, index) => (
                    <motion.div variants={itemVariants} whileHover={{ y: -5 }} key={service.id || index}>
                      <Link to="/services" className="h-full block">
                        <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-100/60 transition-transform group flex flex-col h-full cursor-pointer hover:shadow-xl">
                          <div className={`w-12 h-12 rounded-xl flex justify-center items-center mb-6 ${service.icon_bg_color || 'bg-blue-50'} ${service.icon_text_color || 'text-blue-600'}`}>
                            <DynamicIcon name={service.icon || 'Box'} className="w-6 h-6" />
                          </div>
                          <h3 className="text-xl font-bold text-slate-900 mb-3">{service.name || 'Service'}</h3>
                          <p className="text-slate-500 mb-8 flex-1 leading-relaxed text-sm">{service.description}</p>
                          <div className="flex items-center justify-between text-blue-600 font-bold group-hover:text-blue-700 mt-auto border-t border-slate-50 pt-5">
                            <span>PKR {service.price?.toLocaleString()}{(service.name || '').toLowerCase().includes('bookkeeping') || (service.name || '').toLowerCase().includes('sales tax') ? '/mo' : ''}</span>
                            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-2" />
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))
                ) : (
                  <div className="col-span-1 sm:col-span-2 text-center py-10 text-slate-500">No services available.</div>
                )}

              </motion.div>
            </div>
          </div>
        </section>

        {/* Testimonials Section - Explicit matching to screenshots */}
        <section className="relative min-h-[50vh] lg:min-h-[70vh] flex flex-col justify-center py-12 lg:py-16 bg-[#f4f8fb] overflow-hidden z-20">
          <div className="absolute top-[50%] left-[-5%] lg:left-[5%] -translate-y-1/2">
            <div className="opacity-[0.03] text-blue-900 pointer-events-none">
              <svg width="600" height="600" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 11L8.5 16H5L6.5 11H10ZM18 11L16.5 16H13L14.5 11H18ZM11 5L10 8H6.5L7.5 5H11ZM19 5L18 8H14.5L15.5 5H19Z" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" />
              </svg>
            </div>
          </div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="max-w-[1000px] mx-auto px-4 relative z-10 w-full text-center"
          >
            <motion.h2 variants={itemVariants} className="text-2xl sm:text-3xl md:text-5xl font-extrabold text-slate-900 mb-10 lg:mb-16 tracking-tight">
              Client Success Stories
            </motion.h2>

            <motion.div variants={itemVariants} className="bg-white rounded-[2rem] p-8 lg:p-12 shadow-[0_12px_40px_rgb(0,0,0,0.06)] border border-slate-50/50 mx-auto max-w-3xl transform hover:scale-[1.02] transition-transform duration-500 relative min-h-[300px] flex flex-col justify-center">
              <div className="flex justify-center gap-1.5 mb-8">
                {[...Array(5)].map((_, i) => (
                  <motion.svg 
                    key={i} 
                    initial={{ opacity: 0, scale: 0 }} 
                    animate={{ opacity: 1, scale: 1 }} 
                    transition={{ delay: i * 0.1, type: "spring" }}
                    className="w-6 h-6 text-[#fbbf24] fill-current" 
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </motion.svg>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeReviewIndex}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <p className="text-lg sm:text-xl md:text-2xl text-slate-600 mb-8 lg:mb-10 italic font-medium leading-relaxed max-w-2xl mx-auto min-h-[100px] flex items-center justify-center">
                    "{reviews[activeReviewIndex].text}"
                  </p>

                  <div>
                    <h3 className="text-base sm:text-lg font-extrabold text-[#1a2332] mb-1">{reviews[activeReviewIndex].author}</h3>
                    <p className="text-xs sm:text-sm font-semibold text-blue-500">{reviews[activeReviewIndex].role}</p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>

            <motion.div variants={itemVariants} className="flex justify-center gap-2.5 mt-10">
              {reviews.map((_, i) => (
                <button 
                  key={i}
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => setActiveReviewIndex(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${i === activeReviewIndex ? 'bg-blue-600 w-8' : 'w-2 bg-slate-300 hover:bg-slate-400'}`}
                />
              ))}
            </motion.div>
          </motion.div>
        </section>

      </div> {/* End Watermark BG Container */}

      <section className="relative min-h-[40vh] lg:min-h-[50vh] flex flex-col justify-center bg-blue-600 py-12 lg:py-16 z-20 overflow-hidden">
        {/* Abstract shapes for newsletter background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-10 w-96 h-96 bg-white rounded-full blur-[80px]"></div>
          <div className="absolute bottom-0 left-10 w-64 h-64 bg-white rounded-full blur-[60px]"></div>
        </div>
        
        <div className="max-w-3xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4 lg:mb-6">
            Stay Ahead of the Curve
          </h2>
          <p className="text-sm sm:text-lg md:text-xl text-white mb-8 lg:mb-10 leading-relaxed max-w-2xl mx-auto">
            Join 5,000+ business owners. Get the latest tax updates, financial tips, 
            and exclusive offers delivered straight to your inbox.
          </p>
          
          {subscribeStatus ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 sm:p-8 max-w-xl mx-auto flex flex-col items-center text-center shadow-xl"
            >
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 shadow-lg ${subscribeStatus === 'success' ? 'bg-green-500 shadow-green-500/30' : 'bg-red-500 shadow-red-500/30'}`}>
                {subscribeStatus === 'success' ? (
                  <CheckCircle2 className="h-8 w-8 text-white" />
                ) : (
                  <AlertCircle className="h-8 w-8 text-white" />
                )}
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                {subscribeStatus === 'success' ? 'Awesome!' : 'Oops!'}
              </h3>
              <p className={`text-lg mb-6 ${subscribeStatus === 'success' ? 'text-blue-100' : 'text-red-100 font-medium'}`}>
                {subscribeMessage}
              </p>
              {subscribeStatus === 'error' && (
                <Button 
                   onClick={() => {
                     setSubscribeStatus(null);
                     setSubscribeEmail("");
                   }} 
                   className="bg-white/20 hover:bg-white/30 text-white rounded-full px-8 h-12 font-semibold transition-colors"
                >
                  Try another email
                </Button>
              )}
            </motion.div>
          ) : (
            <form className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto" onSubmit={handleSubscribe}>
              <Input 
                type="email" 
                placeholder="Enter your email address" 
                value={subscribeEmail}
                onChange={(e) => setSubscribeEmail(e.target.value)}
                required
                className="h-14 rounded-full px-6 bg-white border-0 focus-visible:ring-2 focus-visible:ring-blue-300 flex-1 text-black shadow-lg"
              />
              <Button type="submit" disabled={isSubscribing} className="h-14 px-8 rounded-full bg-[#ffc107] hover:bg-[#ffca28] text-slate-900 font-bold text-base transition-colors shrink-0 shadow-lg shadow-yellow-500/20">
                {isSubscribing ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          )}
        </div>
      </section>

      <Suspense fallback={null}>
        <WhatsAppButton />
        <Footer />
      </Suspense>
      <ScrollToTop />
    </div>
  );
};

export default DesktopIndex;